import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth.service';
import { consultantsService, ConsultantService } from '../../services/consultants.service';
import { trainingsService } from '../../services/trainings.service';
import StatCard from '../../components/dashboard/StatCard';
import { 
  Briefcase, 
  Calendar, 
  TrendingUp, 
  Plus,
  Eye,
  Edit,
  MoreHorizontal,
  MessageSquare,
  Star,
  Globe,
  Loader2,
  AlertCircle,
  CheckCircle,
  Crown,
  Users
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

interface DashboardStats {
  totalServices: number;
  activeServices: number;
  totalQuotes: number;
}

interface RecentService {
  id: string;
  title: string;
  status: 'pending' | 'approved' | 'refused'; // Keep original status values
  professionnels: number;
  createdAt: string;
}

interface ConsultantInfo {
  id: number;
  name: string;
  company_name?: string;
  phone_number: string;
  about?: string;
  address?: string;
  website?: string;
  social_media_links?: string;
  field_ids?: number[];
  wilaya_id?: number;
  profile_type: 'training_center' | 'consultant';
}

const ConsultantDashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [consultant, setConsultant] = useState<ConsultantInfo | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalServices: 0,
    activeServices: 0,
    totalQuotes: 0
  });
  const [recentServices, setRecentServices] = useState<RecentService[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [canCreate, setCanCreate] = useState<{ canCreate: boolean; message?: string } | null>(null);
  const [checkingPermission, setCheckingPermission] = useState(false);
  const [showLimitMessage, setShowLimitMessage] = useState(false);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Fetch consultant info (using training center endpoint with profile_type filter)
        const consultantInfo = await authService.getTrainingCenterInfo();
        setConsultant(consultantInfo);

        // Load consultant services
        const servicesData = await consultantsService.getMyServices();
        
        // Calculate stats from services
        const totalServices = servicesData.total;
        const activeServices = servicesData.services.filter(s => s.is_active).length;
        
        setStats({
          totalServices,
          activeServices,
          totalQuotes: 0 // TODO: Implement quote system
        });

        // Load recent services - exclude refused services
        const recentServicesData = servicesData.services
          .filter(service => service.status !== 'refused')
          .slice(0, 5)
          .map(service => ({
            id: service.id,
            title: service.title,
            status: service.status, // Keep original status value for proper comparison
            professionnels: 0, // TODO: Implement professionnel count
            createdAt: service.created_at
          }));
        setRecentServices(recentServicesData);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const checkCreatePermission = async () => {
    try {
      setCheckingPermission(true);
      const result = await trainingsService.canCreateTraining();
      setCanCreate(result);
    } catch (error) {
      setCanCreate({ canCreate: false, message: t('consultant.services.limit.message') });
    } finally {
      setCheckingPermission(false);
    }
  };

  const handleNewServiceClick = async () => {
    if (canCreate === null || checkingPermission) {
      await checkCreatePermission();
    }
    if (canCreate && canCreate.canCreate) {
      navigate('/consultant/services/create');
    } else {
      setShowLimitMessage(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-brand-primary/5 via-brand-secondary/5 to-brand-mint/5 rounded-xl p-4 sm:p-6 border border-brand-primary/20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              {t('consultant.dashboard.welcome', 'Welcome')}, {consultant?.company_name || consultant?.name || t('consultant.dashboard.consultant', 'Consultant')}! 👋
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              {t('consultant.dashboard.subtitle', 'Here is an overview of your consulting activity on PHINLEX')}
            </p>
          </div>
          <button
            onClick={handleNewServiceClick}
            disabled={checkingPermission}
            className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-medium rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all shadow-md text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {checkingPermission ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('consultant.services.button.checking', 'Vérification...')}
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                {t('consultant.dashboard.newService', 'New service')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Limit Reached Alert - Only show after clicking the button */}
      {showLimitMessage && canCreate && !canCreate.canCreate && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-yellow-600 mt-0.5 mr-4 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('consultant.services.limit.title')}
              </h3>
              <p className="text-gray-600 mb-4">
                {canCreate.message || t('consultant.services.limit.message')}
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-900">{t('consultant.services.limit.freePlan.title')}</h4>
                    <p className="text-blue-700 text-sm">{t('consultant.services.limit.freePlan.description')}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => navigate('/consultant/subscriptions')}
                  className="bg-brand-primary text-white px-6 py-3 rounded-lg hover:opacity-90 transition-colors flex items-center justify-center space-x-2"
                >
                  <Crown className="h-5 w-5" />
                  <span>{t('consultant.services.limit.viewPlans')}</span>
                </button>
                <button
                  onClick={() => setShowLimitMessage(false)}
                  className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('consultant.services.limit.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          title={t('consultant.dashboard.stats.totalServices', 'Total services')}
          value={stats.totalServices}
          change={t('consultant.dashboard.stats.changeThisMonth', { defaultValue: '+2 this month' })}
          changeType="positive"
          icon={Briefcase}
          color="brand-primary"
        />
        <StatCard
          title={t('consultant.dashboard.stats.activeServices', 'Active services')}
          value={stats.activeServices}
          change={t('consultant.dashboard.stats.changeThisWeek', { defaultValue: '+1 this week' })}
          changeType="positive"
          icon={Calendar}
          color="brand-secondary"
        />
        <StatCard
          title={t('consultant.dashboard.stats.bookings', 'Service reservations')}
          value={stats.totalQuotes}
          change={t('consultant.dashboard.stats.changeThisMonth', { defaultValue: '+5 this month' })}
          changeType="positive"
          icon={Users}
          color="brand-primary"
        />
      </div>

      {/* Recent Services */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">{t('consultant.dashboard.recent.title', 'Recent services')}</h3>
            <Link
              to="/consultant/services"
              className="text-sm font-medium text-brand-primary hover:text-brand-primary/80 self-start sm:self-auto"
            >
              {t('consultant.dashboard.recent.viewAll', 'View all')}
            </Link>
          </div>
        </div>
        <div className="p-4 sm:p-6">
          {recentServices.length > 0 ? (
            <div className="space-y-4">
              {recentServices.map((service) => (
                <div
                  key={service.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{service.title}</h4>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          service.status === 'approved' 
                            ? 'bg-green-100 text-green-800' 
                            : service.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {service.status === 'approved' && t('common.status.approved', 'Approved')}
                          {service.status === 'pending' && t('common.status.pending', 'Pending')}
                          {service.status !== 'approved' && service.status !== 'pending' && t('common.status.rejected', 'Rejected')}
                        </span>
                        <span>{service.professionnels} {t('consultant.dashboard.recent.interested', 'pharma professionals interested')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="hidden sm:inline">{new Date(service.createdAt).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                        <span className="sm:hidden">{new Date(service.createdAt).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                    <Link
                      to={`/consultant/services/${service.id}`}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title={t('common.view', 'View')}
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                    <Link
                      to={`/consultant/services/${service.id}/edit`}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title={t('common.edit', 'Edit')}
                    >
                      <Edit className="h-4 w-4" />
                    </Link>
                    <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors" title={t('consultant.dashboard.recent.moreOptions', 'More options')}>
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('consultant.dashboard.empty.title', 'No services yet')}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('consultant.dashboard.empty.subtitle', 'Start by creating your first consulting service')}
              </p>
              <div className="mt-6">
                <button
                  onClick={handleNewServiceClick}
                  disabled={checkingPermission}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-medium rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkingPermission ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('consultant.services.button.checking', 'Vérification...')}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('consultant.dashboard.empty.createService', 'Create a service')}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={handleNewServiceClick}
          disabled={checkingPermission}
          className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-8 w-8 text-brand-primary mx-auto mb-2" />
          <h4 className="font-medium text-gray-900">{t('consultant.dashboard.quickActions.newService', 'New service')}</h4>
          <p className="text-sm text-gray-500">{t('consultant.dashboard.quickActions.createService', 'Create a service')}</p>
        </button>
        <Link
          to="/consultant/clients"
          className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center"
        >
          <Users className="h-8 w-8 text-brand-secondary mx-auto mb-2" />
          <h4 className="font-medium text-gray-900">{t('consultant.dashboard.quickActions.bookings', 'Service reservations')}</h4>
          <p className="text-sm text-gray-500">{t('consultant.dashboard.quickActions.manageBookings', 'Manage reservations')}</p>
        </Link>
        {/* Commented out for now - Statistics button
        <Link
          to="/consultant/stats"
          className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center"
        >
          <TrendingUp className="h-8 w-8 text-brand-primary mx-auto mb-2" />
          <h4 className="font-medium text-gray-900">{t('consultant.dashboard.quickActions.stats', 'Statistics')}</h4>
          <p className="text-sm text-gray-500">{t('consultant.dashboard.quickActions.analytics', 'View analytics')}</p>
        </Link>
        */}
        <Link
          to="/consultant/profile"
          className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center"
        >
          <Edit className="h-8 w-8 text-brand-accent mx-auto mb-2" />
          <h4 className="font-medium text-gray-900">{t('consultant.dashboard.quickActions.profile', 'Profile')}</h4>
          <p className="text-sm text-gray-500">{t('consultant.dashboard.quickActions.editProfile', 'Edit profile')}</p>
        </Link>
      </div>
    </div>
  );
};

export default ConsultantDashboardPage;
